// SPDX-License-Identifier: AGPL-3.0-or-later

import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {LimitResolver} from '@app/features/app/utils/LimitResolverAdapter';
import type {Channel} from '@app/features/channel/models/Channel';
import Channels from '@app/features/channel/state/Channels';
import Guilds from '@app/features/guild/state/Guilds';
import {isSoundSourceInUse} from '@app/features/notification/utils/SoundUtils';
import {Logger} from '@app/features/platform/utils/AppLogger';
import UserGuildSettings from '@app/features/user/state/UserGuildSettings';
import {SOUNDBOARD_SOUND_PATH_PREFIX} from '@fluxer/constants/src/SoundboardConstants';

const logger = new Logger('GuildNotificationSound');
const SOUND_HASH = /^[a-f0-9]{64}$/;
const GUILD_ID = /^\d{1,20}$/;
const MAX_FILE_BYTES = 64 * 1024;
const MAX_READY_SOUNDS = 16;
const MAX_PENDING_DOWNLOADS = 16;
const DOWNLOAD_SPREAD_MS = 60_000;
const FAILED_DOWNLOAD_RETRY_MS = 5 * 60_000;

const readyUrls = new Map<string, string>();
const downloads = new Map<string, Promise<string | null>>();
const scheduled = new Map<string, ReturnType<typeof setTimeout>>();
const retryAt = new Map<string, number>();

export function resolveGuildNotificationSoundHash(channel: Channel): string | null {
	const {guildId} = channel;
	if (!guildId || UserGuildSettings.isGuildSoundSuppressed(guildId)) return null;
	const entitled =
		LimitResolver.resolve({
			key: 'feature_guild_notification_sound',
			fallback: 0,
			context: {guildId},
			evaluationContext: 'guild',
		}) > 0;
	if (!entitled) return null;
	const source = channel.isThread() ? Channels.getChannel(channel.parentId ?? '') : channel;
	const hash = source?.notificationSound ?? Guilds.getGuild(guildId)?.notificationSound ?? null;
	return hash !== null && SOUND_HASH.test(hash) ? hash : null;
}

function isValidSource(guildId: string, hash: string): boolean {
	return GUILD_ID.test(guildId) && SOUND_HASH.test(hash);
}

function remember(hash: string, url: string): void {
	readyUrls.set(hash, url);
	for (const [candidate, candidateUrl] of readyUrls) {
		if (readyUrls.size <= MAX_READY_SOUNDS) return;
		// The shared element may be playing this address, and a revoked address cannot be reloaded.
		if (isSoundSourceInUse(candidateUrl)) continue;
		readyUrls.delete(candidate);
		URL.revokeObjectURL(candidateUrl);
	}
}

async function download(guildId: string, hash: string): Promise<string | null> {
	const url = `${RuntimeConfig.mediaEndpoint}/${SOUNDBOARD_SOUND_PATH_PREFIX}/${guildId}/${hash}.mp3`;
	const response = await fetch(url, {cache: 'force-cache', credentials: 'omit'});
	if (!response.ok) return null;
	const blob = await response.blob();
	if (blob.size === 0 || blob.size > MAX_FILE_BYTES) return null;
	const objectUrl = URL.createObjectURL(blob);
	remember(hash, objectUrl);
	return objectUrl;
}

export function loadGuildNotificationSound(guildId: string, hash: string): Promise<string | null> {
	if (!isValidSource(guildId, hash)) return Promise.resolve(null);
	const ready = readyUrls.get(hash);
	if (ready) return Promise.resolve(ready);
	const pending = downloads.get(hash);
	if (pending) return pending;
	const started = download(guildId, hash)
		.catch((error) => {
			logger.debug('Failed to download guild notification sound', {error});
			return null;
		})
		.then((url) => {
			if (url) retryAt.delete(hash);
			else holdOffRetry(hash);
			return url;
		})
		.finally(() => downloads.delete(hash));
	downloads.set(hash, started);
	return started;
}

function holdOffRetry(hash: string): void {
	retryAt.set(hash, Date.now() + FAILED_DOWNLOAD_RETRY_MS);
	if (retryAt.size > MAX_READY_SOUNDS) retryAt.delete(retryAt.keys().next().value!);
}

// A message never waits on the network: an announcement in a large guild would otherwise pull the file from every
// session at once. The first message plays the default sound and the file arrives at a random moment of the next minute.
function scheduleDownload(guildId: string, hash: string): void {
	if (scheduled.has(hash) || downloads.has(hash) || scheduled.size >= MAX_PENDING_DOWNLOADS) return;
	if (Date.now() < (retryAt.get(hash) ?? 0)) return;
	const timer = setTimeout(() => {
		scheduled.delete(hash);
		void loadGuildNotificationSound(guildId, hash);
	}, Math.random() * DOWNLOAD_SPREAD_MS);
	scheduled.set(hash, timer);
}

export function getReadyGuildNotificationSoundUrl(guildId: string, hash: string): string | null {
	if (!isValidSource(guildId, hash)) return null;
	const url = readyUrls.get(hash);
	if (url) {
		readyUrls.delete(hash);
		readyUrls.set(hash, url);
		return url;
	}
	scheduleDownload(guildId, hash);
	return null;
}
